import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const footerStyles = `
  .footer-section {
    background-color: #ffffff00;
    padding: 100px 80px;
  }

  .footer-container {
    display: grid;
    grid-template-columns: minmax(220px, 1.2fr) auto;
    align-items: start;
    gap: 48px;
  }

  .footer-left {
    min-width: 0;
  }

  .footer-left h4 {
    margin: 0 0 14px;
    font-size: 18px;
    font-weight: 600;
    color: #f5f7f0;
  }

  .footer-left p {
    margin: 0 0 8px;
    color: #f5f7f0;
    font-size: 16px;
  }

  .social-iconss {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 18px;
  }

  .social-iconss img {
    width: 60px;
    height: 60px;
    object-fit: contain;
    filter: brightness(0) invert(1);
    opacity: 0.95;
  }

  .footer-middle ul,
  .footer-right ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .footer-middle li,
  .footer-right li {
    margin-bottom: 10px;
    font-size: 14px;
    color: #f5f7f0;
    cursor: pointer;
  }

  .footer-middle li:hover,
  .footer-right li:hover {
    text-decoration: underline;
  }

  .footer-copyright {
    font-size: 14px;
    color: #f5f7f0;
    text-align: center;
    margin-bottom: -80px;
  }

  @media (max-width: 768px) {
    .footer-container {
      margin-top: 40px;
      grid-template-columns: 1fr;
      gap: 20px;
    }

    /* The page reserves space at the bottom of <body> for the floating
       mobile bottom-nav pill (see Navbar.css), coloured with the page's
       light background. When the footer is the last thing on the page,
       that reserved strip showed up as a blank light gap below the
       footer's dark background. Pull the footer down into that strip
       (negative margin) and pad it back out by the same amount so its
       dark background fills it instead of leaving it blank. */
    .footer-section {
      padding-bottom: calc(12px + 96px + env(safe-area-inset-bottom, 0px)) !important;
      margin-bottom: calc(-96px - env(safe-area-inset-bottom, 0px));
    }
  }
`;

function Footer() {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const linkStyle = {
    color: "#f5f7f0",
    textDecoration: "none",
    fontSize: "9px",
    opacity: 0.78,
    lineHeight: "1.2",
  };

  return (
    <footer
      className="footer-section"
      style={{
        backgroundColor: "#1A382D",
        padding: isMobile ? "20px 16px 12px" : "100px 80px",
        minHeight: isMobile ? "auto" : undefined,
      }}
    >
      <style>{footerStyles}</style>
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

          {/* Row 1: contact (left) + links grid (right) */}
          <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end", gap: "12px" }}>

            {/* Contact */}
            <div style={{ display: "flex", flexDirection: "column", gap: "3px", flexShrink: 0 }}>
              <span style={{ color: "#f5f7f0", fontSize: "10px", fontWeight: 700, marginBottom: "2px" }}>{t("pages.footer.contactUs")}</span>
              <span style={{ color: "rgba(245,247,240,0.8)", fontSize: "9px" }}>+374 94 577 585</span>
              <span style={{ color: "rgba(245,247,240,0.7)", fontSize: "8px" }}>greenwicharmenia@gmail.com</span>
            </div>

            {/* Links — 2 columns × 4 rows */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }}>
              <Link to="/whoweare"      style={linkStyle}>{t("pages.footer.whoWeAre")}</Link>
              <Link to="/feed"          style={linkStyle}>{t("pages.footer.feed")}</Link>
              <Link to="/community"     style={linkStyle}>{t("pages.footer.community")}</Link>
              <Link to="/dashboard"     style={linkStyle}>{t("pages.footer.account")}</Link>
              <Link to="/organizations" style={linkStyle}>{t("pages.footer.organizations")}</Link>
              <Link to="/contact"       style={linkStyle}>{t("pages.footer.contact")}</Link>
              <Link to="/programs"      style={linkStyle}>{t("pages.footer.projects")}</Link>
              <Link to="/donate"        style={linkStyle}>{t("pages.footer.donate")}</Link>
            </div>
          </div>

          {/* Row 2: social + copyright */}
          <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid rgba(245,247,240,0.15)", paddingTop: "10px" }}>
            <div style={{ display: "flex", flexDirection: "row", gap: "10px", alignItems: "center" }}>
              <a href="https://www.facebook.com/profile.php?id=61578377498350" target="_blank" rel="noopener noreferrer">
                <img src="/nkarner/10483439.png" alt="Facebook" style={{ width: 38, height: 38, filter: "brightness(0) invert(1)", opacity: 0.85 }} />
              </a>
              <a href="https://www.instagram.com/greenwicharmenia?igsh=czMwdDVqeXcyN3Zs" target="_blank" rel="noopener noreferrer">
                <img src="/nkarner/10483448.png" alt="Instagram" style={{ width: 38, height: 38, filter: "brightness(0) invert(1)", opacity: 0.85 }} />
              </a>
              <a href="https://t.me/+_pwnjwFsOmY3ZjI6" target="_blank" rel="noopener noreferrer">
                <img src="/nkarner/104834488.png" alt="Telegram" style={{ width: 38, height: 38, filter: "brightness(0) invert(1)", opacity: 0.85 }} />
              </a>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
              <div style={{ display: "flex", gap: "10px" }}>
                <Link to="/terms" style={{ color: "rgba(245,247,240,0.6)", fontSize: "9px", textDecoration: "none" }}>{t("pages.footer.terms")}</Link>
                <Link to="/privacy" style={{ color: "rgba(245,247,240,0.6)", fontSize: "9px", textDecoration: "none" }}>{t("pages.footer.privacy")}</Link>
              </div>
              <span style={{ color: "rgba(245,247,240,0.55)", fontSize: "9px" }}>{t("pages.footer.copyright")}</span>
            </div>
          </div>

        </div>
      ) : (
        /* ══ DESKTOP ══ */
        <>
          <div className="footer-container">
            {/* Left: contact info + socials */}
            <div className="footer-left">
              <h4>{t("pages.footer.contactUs")}</h4>
              <p>+374 94 577 585</p>
              <p>greenwicharmenia@gmail.com</p>
              <div className="social-iconss">
                <a href="https://www.facebook.com/profile.php?id=61578377498350&rdid=x7qpXda1buRWCx10&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F17BB9Q4EhF%2F#" target="_blank" rel="noopener noreferrer">
                  <img src="/nkarner/10483439.png" alt="Facebook" />
                </a>
                <a href="https://www.instagram.com/greenwicharmenia?igsh=czMwdDVqeXcyN3Zs" target="_blank" rel="noopener noreferrer">
                  <img src="/nkarner/10483448.png" alt="Instagram" />
                </a>
                <a href="https://t.me/+_pwnjwFsOmY3ZjI6" target="_blank" rel="noopener noreferrer">
                  <img src="/nkarner/104834488.png" alt="Telegram" />
                </a>
              </div>
            </div>

            {/* Right side: two nav columns with a vertical divider between them */}
            <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start" }}>
              {/* Column 1: Who we are, Community, Organizations, Projects */}
              <div className="footer-middle">
                <ul>
                  <li><Link to="/whoweare"      style={{ color: "#f5f7f0", textDecoration: "none" }}>{t("pages.footer.whoWeAre")}</Link></li>
                  <li><Link to="/community"     style={{ color: "#f5f7f0", textDecoration: "none" }}>{t("pages.footer.community")}</Link></li>
                  <li><Link to="/organizations" style={{ color: "#f5f7f0", textDecoration: "none" }}>{t("pages.footer.organizations")}</Link></li>
                  <li><Link to="/programs"      style={{ color: "#f5f7f0", textDecoration: "none" }}>{t("pages.footer.projects")}</Link></li>
                </ul>
              </div>

              {/* Vertical divider */}
              <div style={{
                width: "1px",
                alignSelf: "stretch",
                background: "rgba(245,247,240,0.25)",
                margin: "0 32px",
              }} />

              {/* Column 2: Feed, Account, Contact, Donate */}
              <div className="footer-right" style={{ justifySelf: "unset" }}>
                <ul>
                  <li><Link to="/feed"      style={{ color: "#f5f7f0", textDecoration: "none" }}>{t("pages.footer.feed")}</Link></li>
                  <li><Link to="/dashboard" style={{ color: "#f5f7f0", textDecoration: "none" }}>{t("pages.footer.account")}</Link></li>
                  <li><Link to="/contact"   style={{ color: "#f5f7f0", textDecoration: "none" }}>{t("pages.footer.contact")}</Link></li>
                  <li><Link to="/donate"    style={{ color: "#f5f7f0", textDecoration: "none" }}>{t("pages.footer.donate")}</Link></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-copyright" style={{ marginTop: "20px" }}>
            <div style={{ display: "flex", justifyContent: "center", gap: "18px", marginBottom: "10px" }}>
              <Link to="/terms" style={{ color: "rgba(245,247,240,0.75)", fontSize: "13px", textDecoration: "none" }}>{t("pages.footer.terms")}</Link>
              <Link to="/privacy" style={{ color: "rgba(245,247,240,0.75)", fontSize: "13px", textDecoration: "none" }}>{t("pages.footer.privacy")}</Link>
            </div>
            <p>{t("pages.footer.copyrightLong")}</p>
          </div>
        </>
      )}
    </footer>
  );
}

export default Footer;
